import {
  ActivateSelfFundedOfferResultTypenames,
  ActivateSelfFundedOfferSuccess,
  Mutation,
} from '@varo/graphql-schema-types';
import { useCallback, useEffect, useState } from 'react';
import ActivateSelfFundedOffer from 'services/graphql/mutations/Cashback/ActivateSelfFundedOffer';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { CombinedError, useMutation } from 'urql';

const MAX_RETRY_ATTEMPTS = 3;

const logError = (message: unknown) => {
  logErrorWithContext(message, {
    featureArea: FeatureArea.REWARDS,
    responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
    severity: ErrorSeverity.LOW,
  });
};

const useActivateSelfFundedOffer = () => {
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [, setLastOfferId] = useState<string | null>(null);

  const [mutationResult, executeMutation] = useMutation<Mutation>(
    ActivateSelfFundedOffer,
  );

  const {
    fetching: mutationFetching = false,
    data: mutationData,
    error: mutationError,
  } = mutationResult;

  const activateOffer = useCallback(
    (offerId: string) => {
      setLastOfferId(offerId);
      setRetryAttempt(0);
      void executeMutation({ offerId });
    },
    [executeMutation],
  );

  const retryActivateOffer = useCallback(
    (offerId: string) => {
      if (retryAttempt < MAX_RETRY_ATTEMPTS) {
        setRetryAttempt((prev) => prev + 1);
        void executeMutation({ offerId });
      }
    },
    [executeMutation, retryAttempt],
  );

  // Reset retry count on successful mutation
  useEffect(() => {
    if (mutationData && !mutationError) {
      setRetryAttempt(0);
      setLastOfferId(null);
    }
  }, [mutationData, mutationError]);

  useEffect(() => {
    if (mutationError) {
      logError(mutationError.message);
    } else if (
      mutationData?.activateSelfFundedOffer?.__typename ===
      ActivateSelfFundedOfferResultTypenames.ActivateSelfFundedOfferError
    ) {
      logError(mutationData.activateSelfFundedOffer.message);
    }
  }, [mutationError, mutationData]);

  let activatedOfferData: ActivateSelfFundedOfferSuccess | null = null;
  let activateOfferError: CombinedError | null = null;

  if (mutationError) {
    // Show error immediately (for first 3 attempts, UI will show an alert)
    activateOfferError = mutationError;
  } else if (
    mutationData?.activateSelfFundedOffer?.__typename ===
    ActivateSelfFundedOfferResultTypenames.ActivateSelfFundedOfferSuccess
  ) {
    activatedOfferData = mutationData.activateSelfFundedOffer;
  } else if (
    mutationData?.activateSelfFundedOffer?.__typename ===
    ActivateSelfFundedOfferResultTypenames.ActivateSelfFundedOfferError
  ) {
    // Business errors don't need retries, show immediately
    activateOfferError = {
      name: mutationData.activateSelfFundedOffer.title,
      message: mutationData.activateSelfFundedOffer.message,
      graphQLErrors: [],
    };
  }

  return {
    activatedOfferData,
    mutationFetching: Boolean(mutationFetching),
    activateOfferError,
    activateOffer,
    retryActivateOffer,
    retryAttempt,
    maxRetryAttempts: MAX_RETRY_ATTEMPTS,
  };
};

export default useActivateSelfFundedOffer;
